import { SUN_RAYS } from "@/lib/sunRays";

/**
 * SparkSun — an evolved version of the original Spark Coaching sun mark.
 * Hand-drawn feel: open circular centre, rays of different lengths,
 * scattered dot details, slightly unconventional geometry.
 *
 * `earned` — list of ray keys the user has earned through confirmations.
 * When omitted, the full mark is shown. `fresh` — keys that should draw
 * themselves in once (newly earned).
 */
export const SparkSun = ({
  size = 40,
  earned,
  fresh = [],
  className = "",
  strokeWidth = 1.25,
  color = "currentColor",
  title,
  testId,
}) => {
  const show = (key) => !earned || earned.includes(key);
  const isFresh = (key) => fresh.includes(key);

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      role={title ? "img" : "presentation"}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      data-testid={testId || (earned ? "spark-sun-personal" : undefined)}
      data-rays={earned ? earned.length : undefined}
    >
      {title ? <title>{title}</title> : null}
      <g
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Open circular centre — deliberately not a perfect circle */}
        <path d="M 50 36.5
                 C 58.4 36.5 63.6 41.1 63.6 49.6
                 C 63.6 58.2 58.2 63.5 50 63.5
                 C 41.8 63.5 36.4 58 36.4 49.6
                 C 36.4 41.4 41.6 36.5 50 36.5 Z" />

        {/* Foundation rays — everyone's mark begins here */}
        <line x1="50" y1="26" x2="50" y2="12" />
        <line x1="50" y1="74" x2="50" y2="88" />
        <line x1="26" y1="50" x2="12" y2="50" />
        <line x1="74" y1="50" x2="88" y2="50" />

        {/* Earned rays */}
        {SUN_RAYS.filter(r => r.kind === "ray" && show(r.key)).map(r => (
          <line
            key={r.key}
            data-ray={r.key}
            x1={r.geom[0]} y1={r.geom[1]} x2={r.geom[2]} y2={r.geom[3]}
            pathLength="1"
            className={isFresh(r.key) ? "spark-ray-new" : undefined}
          />
        ))}
      </g>

      {/* Dot details — small and off-centre */}
      <g fill={color}>
        <circle cx="50" cy="8"  r="1.2" />
        <circle cx="92" cy="50" r="1.2" />
        <circle cx="50" cy="92" r="1"   />
        {SUN_RAYS.filter(r => r.kind === "dot" && show(r.key)).map(r => (
          <circle
            key={r.key}
            data-ray={r.key}
            cx={r.geom[0]} cy={r.geom[1]} r={r.geom[2]}
            className={isFresh(r.key) ? "spark-dot-new" : undefined}
          />
        ))}
      </g>
    </svg>
  );
};

/**
 * SunArc — a cropped fragment of the mark. Use as an editorial device at
 * page edges, above key insights, or as a subtle divider.
 */
export const SunArc = ({ size = 260, className = "", color = "currentColor", strokeWidth = 1 }) => (
  <svg viewBox="0 0 260 260" width={size} height={size} className={className} aria-hidden="true">
    <g fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round">
      <path d="M 20 240 Q 130 40 240 240" />
      <line x1="60" y1="220" x2="52" y2="200" />
      <line x1="90" y1="180" x2="80" y2="158" />
      <line x1="130" y1="160" x2="130" y2="138" />
      <line x1="170" y1="180" x2="180" y2="158" />
      <line x1="200" y1="220" x2="208" y2="200" />
    </g>
    <g fill={color}>
      <circle cx="130" cy="130" r="1.4" />
      <circle cx="52" cy="196" r="1.2" />
      <circle cx="208" cy="196" r="1.2" />
    </g>
  </svg>
);

/**
 * GoldThreadLine — a fine, slightly hand-drawn horizontal line used as
 * a divider or as the connective thread between two insights.
 */
export const GoldThreadLine = ({ className = "", color = "#C3A56B" }) => (
  <svg viewBox="0 0 400 8" preserveAspectRatio="none" className={className} aria-hidden="true">
    <path
      d="M 2 4 C 80 1, 160 7, 240 3 S 380 6, 398 4"
      fill="none"
      stroke={color}
      strokeWidth="1"
      strokeLinecap="round"
    />
  </svg>
);

export default SparkSun;
